import React, { useState, useEffect } from 'react';
import { journeyNameType } from '../../view-models';
import {
  NonInteractiveTechnicalProfile,
  OrchestrationStep,
  Precondition,
  SelfAssertedProfile,
  SubJourney,
  UserJourney,
} from '../../xmlParsing/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchResult, setSearchTPResult, setSelectedSearch, getRPComponents } from '../../store';
import CONSTANTS from './../../constants/constants';
const searchOptions = [
  CONSTANTS.CATEGORY.ORCHSTEPNAME, //Search in orch step display names in flowchart
  CONSTANTS.CATEGORY.PRECONDITION, // Search for precondition claims
  CONSTANTS.CATEGORY.INPUTCLAIMS, // Search input claims across the user journey
  CONSTANTS.CATEGORY.OUTPUTCLAIMS, // Search output claims across the user journey
  CONSTANTS.CATEGORY.DISPLAYCLAIMS, // Search display claims across the user journey
  CONSTANTS.CATEGORY.PERSISTEDCLAIMS, // Search persisted claims across the user journey
  CONSTANTS.CATEGORY.PARTNERCLAIMTYPE, // Partner claim type
  CONSTANTS.CATEGORY.DEFAULTVALUE, // default value
  CONSTANTS.CATEGORY.INPUTCLAIMSTRANSNAME, // Search input claims transformations across the user journey
  CONSTANTS.CATEGORY.OUTPUTCLAIMSTRANSNAME, // Search output claims transformations across the user journey
  CONSTANTS.CATEGORY.VALIDATIONTPNAME, // Search validation TP names across the user journey's technical profiles (Won't look inside Validation TP)
];

const useSearchHook = (
  selfAssertedProfiles: SelfAssertedProfile,
  otherProfiles: NonInteractiveTechnicalProfile,
  userJourneys: UserJourney[],
  subJourneys: SubJourney[],
  journeyNameType: journeyNameType,
  selectedRP: string,
) => {
  const [searchCategory, setSearchCategory] = useState<string[]>(searchOptions);
  const [searchIn, setSearchIn] = useState<'rpFiles' | 'userJourney'>('userJourney');
  const [searchText, setSearchText] = useState<string>('');
  const [showSearchDialog, setShowSearchDialog] = useState<boolean>(false);
  const [subJourneyIndexMap, setSubJourneyIndexMap] = useState<Record<string, number>>({});
  const dispatch = useDispatch();
  let tpDataRef: Record<string, any> = {};
  const rpComponents = useSelector(getRPComponents);

  useEffect(() => {
    let _subJourneyIndexMap: Record<string, number> = {};
    subJourneys.forEach((journey: SubJourney, index: number) => {
      _subJourneyIndexMap[journey['id']] = index;
    });
    setSubJourneyIndexMap(_subJourneyIndexMap);
  }, [subJourneys]);

  const searchHandler = (val: string, searchIn: 'rpFiles' | 'userJourney', searchCategory: string[]) => {
    setShowSearchDialog(false);
    dispatch(setSearchResult([]));
    dispatch(setSelectedSearch(null));
    const key = journeyNameType['type'] + journeyNameType['name'];
    tpDataRef = {};
    if (searchCategory.length === 0) {
      return;
    }
    let _finalRes: any[] = [];
    if (val.trim() !== '') {
      setShowSearchDialog(true);
      let _placesToSearch: string[] = ['validationTPContent', 'subJourneys'];
      let _journeys: string[] = [];
      searchCategory.includes(CONSTANTS.CATEGORY.ORCHSTEPNAME) && _placesToSearch.push('orchestrationStepName');
      searchCategory.includes(CONSTANTS.CATEGORY.PRECONDITION) && _placesToSearch.push('preconditions');
      searchCategory.includes(CONSTANTS.CATEGORY.INPUTCLAIMS) && _placesToSearch.push('inputClaims');
      searchCategory.includes(CONSTANTS.CATEGORY.OUTPUTCLAIMS) && _placesToSearch.push('outputClaims');
      searchCategory.includes(CONSTANTS.CATEGORY.DISPLAYCLAIMS) && _placesToSearch.push('displayClaims');
      searchCategory.includes(CONSTANTS.CATEGORY.PERSISTEDCLAIMS) && _placesToSearch.push('persistedClaims');
      searchCategory.includes(CONSTANTS.CATEGORY.PARTNERCLAIMTYPE) && _placesToSearch.push('partnerClaimType');
      searchCategory.includes(CONSTANTS.CATEGORY.DEFAULTVALUE) && _placesToSearch.push('defaultValue');
      searchCategory.includes(CONSTANTS.CATEGORY.INPUTCLAIMSTRANSNAME) &&
        _placesToSearch.push('inputClaimsTransformations');
      searchCategory.includes(CONSTANTS.CATEGORY.OUTPUTCLAIMSTRANSNAME) &&
        _placesToSearch.push('outputClaimsTransformations');
      searchCategory.includes(CONSTANTS.CATEGORY.VALIDATIONTPNAME) && _placesToSearch.push('validationTPName');
      // Search in selected UserJourneys
      if (searchIn === 'rpFiles') {
        Object.keys(rpComponents).forEach((rpFileName: string) => {
          _journeys.push(rpComponents[rpFileName]['defaultUserJourney']);
        });
      } else if (searchIn === 'userJourney') {
        _journeys.push(journeyNameType['name']);
      }

      if (journeyNameType['type'] === 'UserJourneys') {
        userJourneys.forEach((journey: UserJourney) => {
          if (_journeys.includes(journey['id'])) {
            _finalRes = searchJourneyUtil(journey, _placesToSearch, val, {
              type: 'UserJourneys',
              journeyId: journey['id'],
            });
          }
        });
      } else {
        // Search in SubJourneys
        subJourneys.forEach((journey: SubJourney) => {
          if (journey['id'] === journeyNameType['name']) {
            _finalRes = searchJourneyUtil(journey, _placesToSearch, val, {
              type: 'SubJourneys',
              journeyId: journey['id'],
            });
          }
        });
      }
      dispatch(setSearchResult(_finalRes));
      dispatch(setSearchTPResult(tpDataRef));
      dispatch(setSelectedSearch(null));
    }
  };
  const handleTextChange = (value: string) => {
    setSearchText(value);
    searchHandler(value, searchIn, searchCategory);
  };
  const handleCategoryChange = (_searchIn: 'rpFiles' | 'userJourney', value: string[]) => {
    if (value[value.length - 1] === 'all') {
      const allSelected: boolean = searchCategory.length === searchOptions.length;
      setSearchIn(_searchIn);
      setSearchCategory(allSelected ? [] : searchOptions);
      searchHandler(searchText, _searchIn, allSelected ? [] : searchOptions);
      return;
    }
    setSearchIn(_searchIn);
    setSearchCategory(value);
    searchHandler(searchText, _searchIn, value);
  };

  const searchTPUtil = (tpData: any, _placesToSearch: string[], val: string) => {
    let tpObj: any = {};
    // Search Claim Name
    ['inputClaims', 'outputClaims', 'displayClaims', 'persistedClaims'].forEach((type: string) => {
      (tpData[type] || []).forEach((claim: any) => {
        if (
          (_placesToSearch.includes(type) && claim['claimTypeReferenceId'].toLowerCase().includes(val.toLowerCase())) ||
          (claim.hasOwnProperty('partnerClaimType') &&
            _placesToSearch.includes('partnerClaimType') &&
            claim['partnerClaimType'].toLowerCase().includes(val.toLowerCase())) ||
          (claim.hasOwnProperty('defaultValue') &&
            _placesToSearch.includes('defaultValue') &&
            claim['defaultValue'].toLowerCase().includes(val.toLowerCase()))
        ) {
          if (!tpObj.hasOwnProperty(type)) {
            tpObj[type] = {};
          }
          tpObj[type][claim['claimTypeReferenceId']] = { ...claim, expand: false };
        }
      });
    });

    // Search Claim Transformation
    ['inputClaimsTransformations', 'outputClaimsTransformations'].forEach((type: string) => {
      const index = (tpData.hasOwnProperty(type) ? tpData[type] : []).findIndex((el: string) =>
        el.toLowerCase().includes(val.toLowerCase()),
      );
      if (_placesToSearch.includes(type) && index >= 0) {
        if (!tpObj.hasOwnProperty(type)) {
          tpObj[type] = [];
        }
        tpObj[type].push(tpData[type][index]);
      }
    });

    // Search validationTPName
    if (_placesToSearch.includes('validationTPName')) {
      (tpData?.validationTechnicalProfiles || []).forEach((vtp: any) => {
        if (vtp['id'].toLowerCase().includes(val.toLowerCase())) {
          if (!tpObj.hasOwnProperty('validationTechnicalProfiles')) {
            tpObj['validationTechnicalProfiles'] = [];
          }
          tpObj['validationTechnicalProfiles'].push(vtp['id']);
        }
      });
    }

    // Search validationTPContent
    if (_placesToSearch.includes('validationTPContent')) {
      (tpData?.validationTechnicalProfiles || []).forEach((vtp: any) => {
        let vtpData: any = {};
        if (tpDataRef.hasOwnProperty(vtp['id']) && Object.keys(tpDataRef[vtp['id']]).length > 0) {
          vtpData = tpDataRef[vtp['id']];
        } else {
          // @ts-ignore
          const _vtpData = otherProfiles[vtp['id']];
          vtpData = searchTPUtil(_vtpData, _placesToSearch, val);
          if (Object.keys(vtpData).length > 0) {
            tpDataRef[vtp['id']] = vtpData;
          }
        }
        if (Object.keys(vtpData).length > 0) {
          if (!tpObj.hasOwnProperty('validationTechnicalProfiles')) {
            tpObj['validationTechnicalProfiles'] = [];
          }
          !tpObj['validationTechnicalProfiles'].includes(vtp['id']) &&
            tpObj['validationTechnicalProfiles'].push(vtp['id']);
        }
      });
    }
    return tpObj;
  };

  const shouldGetTPData = (placesToSearch: string[]) => {
    // Check if TPData needs to be fetched
    let getTPData: boolean = false;
    const options = [
      'inputClaims',
      'outputClaims',
      'displayClaims',
      'persistedClaims',
      'partnerClaimType',
      'defaultValue',
      'inputClaimsTransformations',
      'outputClaimsTransformations',
      'validationTPName',
    ];
    for (let i = 0; i < options.length; i++) {
      if (placesToSearch.includes(options[i])) {
        getTPData = true;
        break;
      }
    }
    return getTPData;
  };

  const getTPData = (tpId: string, _placesToSearch: string[], val: string, _stepObj: any) => {
    let tpData: any = {};
    if (tpDataRef.hasOwnProperty(tpId) && Object.keys(tpDataRef[tpId]).length > 0) {
      tpData = tpDataRef[tpId];
    } else {
      // @ts-ignore
      const _tpData: any = selfAssertedProfiles[tpId] || otherProfiles[tpId];
      tpData = searchTPUtil(_tpData, _placesToSearch, val);

      if (Object.keys(tpData).length > 0) {
        tpDataRef[tpId] = tpData;
      }
    }
    if (Object.keys(tpData).length > 0) {
      _stepObj['highlightStep'] = true;
      _stepObj['orchStepName'] = tpId;
    }
    return _stepObj;
  };

  const searchJourneyUtil = (
    journey: UserJourney | SubJourney,
    _placesToSearch: string[],
    val: string,
    addObj: any,
  ) => {
    let finalResUJ: any = [];
    (journey['orchestrationSteps'] || []).forEach((step: OrchestrationStep, orchIndex: number) => {
      // Search Preconditions
      if (_placesToSearch.includes('preconditions')) {
        step.preconditions.forEach((precondition: Precondition, preIndex: number) => {
          let _precObj: any = { ...addObj };
          if (precondition['values'][0].toLowerCase().includes(val.toLowerCase())) {
            _precObj['precondition'] = precondition['values'][0];
            _precObj['preconditionId'] = 'precondition' + '-' + orchIndex.toString() + '-' + preIndex.toString();
            _precObj['highlightPreCondition'] = true;
            finalResUJ.push(_precObj);
          }
        });
      }

      if (step.subJourneyToInvoke !== '') {
        // Search orchestration steps
        if (
          _placesToSearch.includes('orchestrationStepName') &&
          ('InvokeSubJourney_' + step.subJourneyToInvoke).toLowerCase().includes(val.toLowerCase())
        ) {
          let _stepObj: any = { ...addObj };
          _stepObj['orchStepName'] = 'InvokeSubJourney_' + step.subJourneyToInvoke;
          _stepObj['highlightStep'] = true;
          finalResUJ.push(_stepObj);
        }
        // Search SubJourneys
        if (_placesToSearch.includes('subJourneys')) {
          const finalResSJ: any = searchJourneyUtil(
            subJourneys[subJourneyIndexMap[step.subJourneyToInvoke]],
            _placesToSearch,
            val,
            {
              type: 'SubJourneys',
              journeyId: step.subJourneyToInvoke,
            },
          );
          finalResUJ = [...finalResUJ, ...finalResSJ];
        }
      } else {
        let _stepObj: any;
        let tpId: string;

        if (step['claimsExchanges'].length > 0) {
          step['claimsExchanges'].forEach((claimExchange: any) => {
            _stepObj = { ...addObj };
            tpId = claimExchange['technicalProfilerefrenceId'];
            // Search orchestration steps
            if (tpId != '' && _placesToSearch.includes('orchestrationStepName')) {
              _stepObj['orchStepName'] = tpId;
              if (!_stepObj['highlightStep'])
                _stepObj['highlightStep'] = tpId.toLowerCase().includes(val.toLowerCase()) ? true : false;
            }
            // get the TPData for the orchStep
            if (tpId != '' && shouldGetTPData(_placesToSearch)) {
              _stepObj = getTPData(tpId, _placesToSearch, val, _stepObj);
            }
            _stepObj['highlightStep'] && finalResUJ.push(_stepObj);
          });
        } else {
          _stepObj = { ...addObj };
          tpId = step['cpimIssuerTechnicalProfileReferenceId'];
          // Search orchestration steps
          if (tpId != '' && _placesToSearch.includes('orchestrationStepName')) {
            _stepObj['orchStepName'] = tpId;
            if (!_stepObj['highlightStep'])
              _stepObj['highlightStep'] = tpId.toLowerCase().includes(val.toLowerCase()) ? true : false;
          }
          // get the TPData for the orchStep
          if (tpId != '' && shouldGetTPData(_placesToSearch)) {
            _stepObj = getTPData(tpId, _placesToSearch, val, _stepObj);
          }
          _stepObj['highlightStep'] && finalResUJ.push(_stepObj);
        }
      }
    });
    return finalResUJ;
  };

  return {
    searchOptions,
    searchIn,
    searchCategory,
    searchText,
    showSearchDialog,
    setShowSearchDialog,
    setSearchText,
    handleTextChange,
    handleCategoryChange,
  };
};
export default useSearchHook;
